import { inject, Injectable } from '@angular/core';
import { EnvironmentConfig } from '../models/config.model';
import { ConfigService } from './config.service';

/**
 * Silent no-op function for production environments
 * Prevents any console output that could expose sensitive data
 */
const noop = (...args: any[]) => {};

/**
 * Console Logger Service with production-safe logging
 * 
 * In PRODUCTION:
 * - log() and debug() are suppressed (dealers won't see internal debug info)
 * - info() is suppressed (prevents API/cache data exposure)
 * - warn() shows generic warnings without sensitive data
 * - error() always shows (critical for production debugging)
 * 
 * In DEVELOPMENT:
 * - All logging methods work normally for developer debugging
 */
@Injectable({
  providedIn: 'root'
})
export class ConsoleLoggerService {
  private config: EnvironmentConfig;

  constructor() {
    this.config = inject(ConfigService);
  }

  /**
   * General logging - SUPPRESSED in production
   * Use for general debugging, API calls, data flow
   */
  get log() {
    if (!this.config.production) {
      // tslint:disable-next-line:no-console
      return console.log.bind(console);
    }
    return noop;
  }

  /**
   * Debug logging - SUPPRESSED in production
   * Use for detailed debugging, cache inspection, data dumps
   */
  get debug() {
    if (!this.config.production) {
      // tslint:disable-next-line:no-console
      return console.debug.bind(console);
    }
    return noop;
  }

  /**
   * Info logging - SUPPRESSED in production
   * Use for informational messages, success notifications
   */
  get info() {
    if (!this.config.production) {
      // tslint:disable-next-line:no-console
      return console.info.bind(console);
    }
    return noop;
  }

  /**
   * Warning logging - ALWAYS SHOWS but sanitized in production
   * Use for non-critical issues that should be visible
   */
  get warn() {
    if (!this.config.production) {
      return console.warn.bind(console);
    }
    // In production, show only the warning category without sensitive details
    return (category: string, ...args: any[]) => {
      console.warn(`[Warning: ${category}]`);
    };
  }

  /**
   * Error logging - ALWAYS SHOWS
   * Use for critical errors that need visibility in all environments
   * Note: Error messages should not contain sensitive user/dealer data
   */
  get error() {
    return console.error.bind(console);
  }

  /**
   * Production-safe logging for dealer actions
   * Logs full details in dev, only action name in production
   * 
   * @example
   * this.logger.logAction('MFE_VERSION_LOADED', { dealer: 12345, version: 'current' });
   * // Dev: [MFE_VERSION_LOADED] { dealer: 12345, version: 'current' }
   * // Prod: [MFE_VERSION_LOADED]
   */
  logAction(action: string, data?: any) {
    if (!this.config.production) {
      console.log(`[${action}]`, data);
    } else {
      // Log action without sensitive data in production
      console.log(`[${action}]`);
    }
  }

  /**
   * Production-safe logging for API calls
   * Shows full details in dev, sanitized in production
   */
  logApiCall(method: string, url: string, data?: any) {
    if (!this.config.production) {
      console.log(`[API] ${method} ${url}`, data);
    }
    // Suppressed in production to prevent API endpoint exposure
  }

  /**
   * Production-safe logging for cache operations
   * Shows full details in dev, suppressed in production
   */
  logCache(operation: string, key: string, value?: any) {
    if (!this.config.production) {
      console.log(`[Cache] ${operation} - ${key}`, value);
    }
    // Suppressed in production to prevent cache data exposure
  }
}
